import {
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX,
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN,
  CREATIVE_TEMPLATE_STRIP_MAX_EDGE,
  CREATIVE_TEMPLATE_STRIP_MAX_PIXELS,
} from "../../../utils/creativeTemplateLayout.js";
import { normalizeCrop } from "../editor/layerCrop.js";

const MIN_NODE_SIZE = 1;

export const isValidContinuousSlideCount = ({ slideCount } = {}) =>
  Number.isInteger(slideCount) &&
  slideCount >= CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN &&
  slideCount <= CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX;

export const getContinuousSlideCount = ({ layoutConfigurations } = {}) => {
  const parsed = Number(layoutConfigurations?.continuousCarousel?.slideCount);
  if (!Number.isInteger(parsed)) return CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN;
  return Math.min(
    CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX,
    Math.max(CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN, parsed)
  );
};

export const getContinuousSlideWidth = ({ width, slideCount } = {}) => {
  const total = Number(width);
  if (!isValidContinuousSlideCount({ slideCount })) return null;
  if (!Number.isInteger(total) || total <= 0) return null;
  if (total % slideCount !== 0) return null;
  return total / slideCount;
};

export const listSeamOffsets = ({ slideWidth, slideCount } = {}) =>
  Array.from(
    { length: Math.max(0, slideCount - 1) },
    (_, index) => slideWidth * (index + 1)
  );

export const buildStripSize = ({
  slideWidth,
  slideHeight,
  slideCount,
} = {}) => ({
  width: Math.round(Number(slideWidth) * Number(slideCount)),
  height: Math.round(Number(slideHeight)),
});

export const buildStripArtboard = ({
  slideWidth,
  slideHeight,
  slideCount,
  label,
} = {}) => ({
  label,
  ...buildStripSize({ slideWidth, slideHeight, slideCount }),
  nodes: {},
  nodeLayouts: {},
});

export const isStripWithinLimits = ({ width, height } = {}) => {
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return false;
  if (w > CREATIVE_TEMPLATE_STRIP_MAX_EDGE) return false;
  if (h > CREATIVE_TEMPLATE_STRIP_MAX_EDGE) return false;
  return w * h <= CREATIVE_TEMPLATE_STRIP_MAX_PIXELS;
};

const resizeBackgroundLayouts = ({
  nodes = {},
  nodeLayouts = {},
  width,
  height,
}) => {
  const next = { ...nodeLayouts };
  Object.entries(nodes).forEach(([nodeID, node]) => {
    if (!node?.isBackground || !next[nodeID]) return;
    next[nodeID] = {
      ...next[nodeID],
      x: 0,
      y: 0,
      width,
      height: Math.round(Number(height) || next[nodeID].height || 0),
    };
  });
  return next;
};

export const continuousSlideGuideID = (index) => `slide_${index + 1}`;

// Guides are keyed by slot (`slide_1`…), so removing a middle slide
// has to move the later guides left with the content.
export const shiftSlideGuides = ({
  slideGuides = {},
  removedIndex,
  slideCount,
} = {}) => {
  const next = {};
  for (let index = 0; index < slideCount; index += 1) {
    if (index === removedIndex) continue;
    const destination = index > removedIndex ? index - 1 : index;
    const key = slideGuides[continuousSlideGuideID(index)];
    if (key) next[continuousSlideGuideID(destination)] = key;
  }
  return next;
};

export const listContinuousGuideSlots = ({
  artboard,
  assets = {},
  slideCount,
} = {}) => {
  const guides = artboard?.slideGuides || {};
  const count = Number(slideCount) || 0;
  if (count < 1) return [];
  const slots = [];
  for (let index = 0; index < count; index += 1) {
    const url = assets[guides[continuousSlideGuideID(index)]]?.url || "";
    if (!url) continue;
    slots.push({
      index,
      url,
      leftRatio: index / count,
      widthRatio: 1 / count,
    });
  }
  return slots;
};

export const addStripSlide = ({
  nodes = {},
  nodeLayouts = {},
  slideCount,
  slideWidth,
  height,
} = {}) => {
  const nextCount = slideCount + 1;
  const width = Math.round(slideWidth * nextCount);
  if (
    !isValidContinuousSlideCount({ slideCount: nextCount }) ||
    !isStripWithinLimits({ width, height })
  ) {
    return {
      changed: false,
      slideCount,
      width: Math.round(slideWidth * slideCount),
      nodes,
      nodeLayouts,
    };
  }
  return {
    changed: true,
    slideCount: nextCount,
    width,
    nodes,
    nodeLayouts: resizeBackgroundLayouts({ nodes, nodeLayouts, width, height }),
  };
};

const isRotated = (layout) => Math.abs(Number(layout?.rotation) || 0) > 0.001;

const clipImageToRange = ({ node, layout, from, to }) => {
  const base = normalizeCrop(node.crop);
  const f0 = (from - layout.x) / layout.width;
  const f1 = (to - layout.x) / layout.width;
  return {
    ...node,
    crop: {
      ...base,
      x: base.x + f0 * base.width,
      width: (f1 - f0) * base.width,
    },
  };
};

export const removeStripSlide = ({
  nodes = {},
  nodeLayouts = {},
  slideIndex,
  slideCount,
  slideWidth,
  height,
} = {}) => {
  const width = Math.round(slideWidth * slideCount);
  const unchanged = { changed: false, slideCount, width, nodes, nodeLayouts };
  if (
    !Number.isInteger(slideIndex) ||
    slideIndex < 0 ||
    slideIndex >= slideCount ||
    slideCount <= CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN
  ) {
    return unchanged;
  }
  const zoneStart = slideIndex * slideWidth;
  const zoneEnd = zoneStart + slideWidth;
  const nextWidth = width - slideWidth;
  const nextNodes = {};
  const nextLayouts = {};
  const removedNodeIDs = [];

  Object.entries(nodes).forEach(([nodeID, node]) => {
    const layout = nodeLayouts[nodeID];
    if (!layout || node?.isBackground) {
      nextNodes[nodeID] = node;
      if (layout) nextLayouts[nodeID] = layout;
      return;
    }
    const left = layout.x;
    const right = layout.x + layout.width;
    if (right <= zoneStart) {
      nextNodes[nodeID] = node;
      nextLayouts[nodeID] = layout;
      return;
    }
    if (left >= zoneEnd) {
      nextNodes[nodeID] = node;
      nextLayouts[nodeID] = { ...layout, x: left - slideWidth };
      return;
    }
    if (left >= zoneStart && right <= zoneEnd) {
      removedNodeIDs.push(nodeID);
      return;
    }
    const keepsLeft = left < zoneStart;
    const keepsRight = right > zoneEnd;
    const leftSpan = zoneStart - left;
    const rightSpan = right - zoneEnd;
    const canClip = node.type === "image" && !isRotated(layout);

    if (keepsLeft && keepsRight && !canClip) {
      nextNodes[nodeID] = node;
      nextLayouts[nodeID] = {
        ...layout,
        width: Math.max(MIN_NODE_SIZE, layout.width - slideWidth),
      };
      return;
    }
    const useLeft = keepsLeft && (!keepsRight || leftSpan >= rightSpan);
    const from = useLeft ? left : zoneEnd;
    const to = useLeft ? zoneStart : right;
    const nextLayout = {
      ...layout,
      x: useLeft ? left : zoneStart,
      width: Math.max(MIN_NODE_SIZE, to - from),
    };
    nextNodes[nodeID] = canClip
      ? clipImageToRange({ node, layout, from, to })
      : node;
    nextLayouts[nodeID] = nextLayout;
  });

  return {
    changed: true,
    slideCount: slideCount - 1,
    width: nextWidth,
    removedNodeIDs,
    nodes: nextNodes,
    nodeLayouts: resizeBackgroundLayouts({
      nodes: nextNodes,
      nodeLayouts: nextLayouts,
      width: nextWidth,
      height,
    }),
  };
};
